import { isAxiosError } from 'axios'

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Ungültige Anfrage',
  401: 'Authentifizierung erforderlich',
  403: 'Zugriff verweigert',
  404: 'Ressource nicht gefunden',
  409: 'Konflikt bei der Verarbeitung',
  422: 'Validierungsfehler',
  429: 'Zu viele Anfragen',
}

const GENERIC_ERROR = 'Ein unerwarteter Fehler ist aufgetreten'
const SERVER_ERROR = 'Service vorübergehend nicht verfügbar'

/**
 * Machine-readable error code from the backend's problem body, for example
 * `VOICE_IN_USE`. Returns null for anything that is not an API error.
 */
export function getErrorCode(error: unknown): string | null {
  if (!isAxiosError(error)) return null
  const code = error.response?.data?.error_code
  return typeof code === 'string' ? code : null
}

/**
 * The `context` object the backend attaches to a problem body. Its shape
 * depends on the error code, so callers read the fields they expect.
 */
export function getErrorContext(error: unknown): Record<string, unknown> | null {
  if (!isAxiosError(error)) return null
  const context = error.response?.data?.context
  return context && typeof context === 'object' ? (context as Record<string, unknown>) : null
}

/**
 * Extracts a user-facing German error message from an error.
 * For 4xx errors, uses the backend's localized `detail` field.
 * For 5xx errors, returns a generic message (detail is stripped by the interceptor).
 */
export function getErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    return error instanceof Error ? error.message : GENERIC_ERROR
  }

  const status = error.response?.status
  const data = error.response?.data

  if (status && status >= 500) {
    return SERVER_ERROR
  }

  if (data?.detail) {
    if (typeof data.detail === 'string') {
      return data.detail
    }
    // 422 validation: detail is ValidationError[]
    if (Array.isArray(data.detail)) {
      return data.detail
        .map((e: { msg: string; loc?: (string | number)[] }) => {
          const field = e.loc?.slice(1).join('.')
          return field ? `${field}: ${e.msg}` : e.msg
        })
        .join(', ')
    }
  }

  if (status && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status]
  }

  return GENERIC_ERROR
}
